/**
 * UI-facing music state: the listener's own volume / mute (per device), what
 * is playing, whether the browser is still blocking audio, and which panels
 * are open. Tiny and dependency-free so the HUD can import it without
 * pulling in the audio runtime (lazy: components/music/MusicRuntime).
 */
import { create } from 'zustand';
import type { MusicPlayState } from '../../utils/musicSync';

const VOL_KEY = 'gathrxr.music.volume';
const MUTE_KEY = 'gathrxr.music.muted';

function readNum(key: string, d: number): number {
  try { const v = localStorage.getItem(key); const n = v == null ? NaN : Number(v); return Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : d; } catch { return d; }
}
function readBool(key: string): boolean {
  try { return localStorage.getItem(key) === '1'; } catch { return false; }
}
function write(key: string, v: string) {
  try { localStorage.setItem(key, v); } catch { /* private mode / blocked storage */ }
}

export interface NowPlaying {
  key: string;
  trackId: string;
  title: string;
  artist?: string;
  url: string;
  source: 'library' | 'upload';
  playing: boolean;
  index: number;
  count: number;
}

interface MusicUiState {
  /** Listener volume 0..1 (this device). */
  volume: number;
  muted: boolean;
  /** The browser refused to start audio: show "Tap for music". */
  blocked: boolean;
  /** May change shared playback (world owner / invited builder / admin). */
  isHost: boolean;
  /** World soundtrack status (null = no world music). */
  world: NowPlaying | null;
  /** Speakers within earshot, nearest first. */
  speakers: NowPlaying[];
  /** Shared playback states by key ('world', 'speaker:<id>'). */
  states: Record<string, MusicPlayState>;
  /** Track ids an admin has hidden (never played). */
  hiddenIds: Record<string, true>;
  hiddenUrls: Record<string, true>;
  /** Desktop/phone music popover. */
  panelOpen: boolean;
  /** Track being reported (opens the report dialog). */
  reporting: NowPlaying | null;
  /** Playlist editor (world music) open as a dialog (play mode). */
  editorOpen: boolean;
  /** A short message after a host action failed / was blocked. */
  notice: string | null;
}

export const useMusicStore = create<MusicUiState>(() => ({
  volume: readNum(VOL_KEY, 0.8),
  muted: readBool(MUTE_KEY),
  blocked: false,
  isHost: false,
  world: null,
  speakers: [],
  states: {},
  hiddenIds: {},
  hiddenUrls: {},
  panelOpen: false,
  reporting: null,
  editorOpen: false,
  notice: null,
}));

export function setMusicVolume(v: number) {
  const volume = Math.min(1, Math.max(0, Number.isFinite(v) ? v : 0));
  useMusicStore.setState({ volume, ...(volume > 0 ? { muted: false } : {}) });
  write(VOL_KEY, String(Math.round(volume * 100) / 100));
  if (volume > 0) write(MUTE_KEY, '0');
}

export function setMusicMuted(muted: boolean) {
  useMusicStore.setState({ muted });
  write(MUTE_KEY, muted ? '1' : '0');
}

export const toggleMusicMuted = () => setMusicMuted(!useMusicStore.getState().muted);

export const openMusicPanel = (open = !useMusicStore.getState().panelOpen) => useMusicStore.setState({ panelOpen: open });

let noticeTimer: ReturnType<typeof setTimeout> | null = null;
export function musicNotice(text: string | null, ms = 3500) {
  if (noticeTimer) clearTimeout(noticeTimer);
  useMusicStore.setState({ notice: text });
  if (text) noticeTimer = setTimeout(() => useMusicStore.setState({ notice: null }), ms);
}

/** Anything to listen to in this world right now? (shows the HUD button) */
export const hasAnyMusic = (s: Pick<MusicUiState, 'world' | 'speakers'>) => !!s.world || s.speakers.length > 0;
